/**
 * How an entity/page behaves relative to the browser's preferred organization (US 27702).
 *
 * - 'local-only':   the page's core depends on the offline-first sync cycle (Firestore
 *                   writes + service-worker push/pull). It only works in the preferred
 *                   organization and is blocked everywhere else.
 * - 'local-only-when-offline-first':
 *                   'local-only' while the organization's "Save order offline first"
 *                   setting applies to the current user, 'both' otherwise (Bug 28417:
 *                   a new Order 2.5 is written locally only under that setting).
 * - 'server-ready': the page only works against the server and is blocked in the
 *                   preferred organization (none declared yet; supported for the future).
 * - 'both':         works everywhere. In the preferred organization it may read local
 *                   data first; in any other organization it must read from the server
 *                   directly (see DataSourceService).
 */
export type EntityReadiness = 'local-only' | 'local-only-when-offline-first' | 'server-ready' | 'both';

export type EntityReadinessKey =
  | 'posV3'
  | 'posV25'
  | 'cashTransactions'
  | 'registerOperations'
  | 'offlineSalesOrdersV25'
  | 'desktopLocalData';

export const ENTITY_READINESS: Record<EntityReadinessKey, EntityReadiness> = {
  posV3: 'local-only',
  posV25: 'local-only-when-offline-first',
  cashTransactions: 'local-only',
  registerOperations: 'local-only',
  offlineSalesOrdersV25: 'local-only',
  desktopLocalData: 'local-only'
};

/** Entities not declared in the registry work everywhere ('both'). */
export function getEntityReadiness(key: string | undefined): EntityReadiness {
  if (!key) return 'both';
  return (ENTITY_READINESS as Record<string, EntityReadiness>)[key] ?? 'both';
}
